import React, { useEffect, useRef, useState } from 'react'
import ReactToPrint from 'react-to-print'
import { Body2, Heading3 } from '@/components/ui/typography'
import { ClientVerificationReportDetails } from 'api/Clients/Types/ClientVerificationReportDetails'
import { PrinterFilled } from 'components/Icon'
import { Spinner } from 'components/Spinner'
import styled from 'styled-components'
import { useApiClient } from 'utils/api/ApiClient'
import { ClientsApiClient } from 'utils/api/clientsApi'
import { formatLocalDateUTC } from 'utils/date'

interface IProps {
  reportId: string
  client: string
}

export const RemoveIcon: React.FC = () => {
  return (
    <svg className='svg-icon' viewBox='0 0 20 20'>
      <path
        fill='#1b2337'
        d='M15.898,4.045c-0.271-0.272-0.713-0.272-0.986,0l-4.71,4.711L5.493,4.045c-0.272-0.272-0.714-0.272-0.986,0s-0.272,0.714,0,0.986l4.709,4.711l-4.71,4.711c-0.272,0.271-0.272,0.713,0,0.986c0.136,0.136,0.314,0.203,0.492,0.203c0.179,0,0.357-0.067,0.493-0.203l4.711-4.711l4.71,4.711c0.137,0.136,0.314,0.203,0.494,0.203c0.178,0,0.355-0.067,0.492-0.203c0.273-0.273,0.273-0.715,0-0.986l-4.711-4.711l4.711-4.711C16.172,4.759,16.172,4.317,15.898,4.045z'
      />
    </svg>
  )
}

export const CheckmarkIcon: React.FC = () => {
  return (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      width='8'
      height='8'
      viewBox='0 0 24 24'
      fill={'#00be2c'}
    >
      <path d='M20.285 2l-11.285 11.567-5.286-5.011-3.714 3.716 9 8.728 15-15.285z' />
    </svg>
  )
}

const Wrapper = styled.div`
  .print {
    display: flex;
    margin-right: 2em;
    justify-content: flex-end;
  }
  svg {
    width: 24px;
    height: 24px;
    margin-left: auto;
  }
`

const ReportWrapper = styled.div`
  width: 95%;
  display: flex;
  flex-direction: column;
  margin: 1em;
  header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    margin: 2rem;
    span {
      margin: 0 0.25em;
    }
  }
  svg {
    height: 24px;
    width: 24px;
  }
  td,
  th {
    border-color: black;
    border-style: solid;
    border-width: 1px;
    text-align: center;
    font-family: Arial, sans-serif;
    font-size: 14px;
    overflow: hidden;
    padding: 10px 5px;
    word-break: normal;
    vertical-align: top;
    > span:last-child {
      font-weight: bold;
    }
  }
  td {
    text-align: left;
  }
  h3 {
    margin: 2rem;
    display: flex;
    align-items: center;
    justify-content: center;
  }
`

const ClientDetailsVerificationReportDetails: React.FC<IProps> = ({ reportId, client }) => {
  const clientsApiClient = useApiClient(ClientsApiClient)
  const [reportDetails, setReportDetails] = useState<ClientVerificationReportDetails | null>(null)
  const ref = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const fetchData = async () => {
      try {
        const result = await clientsApiClient.getClientIdentityVerificationReportById(reportId)
        setReportDetails(result)
      } catch (error) {
        console.error(error)
      }
    }
    fetchData()
  }, [reportId]) // eslint-disable-line react-hooks/exhaustive-deps

  if (!reportDetails) {
    return <Spinner />
  }
  return (
    <Wrapper>
      <div className='print'>
        <ReactToPrint
          documentTitle={`${client} - ${reportDetails.verifyResult.record.recordStatus}`}
          trigger={() => <PrinterFilled />}
          content={() => ref.current}
        />
      </div>
      <ReportWrapper ref={ref}>
        <header>
          <Body2>
            Client:{' '}
            <Body2 as='span' bold className='mx-1'>
              {client}
            </Body2>
          </Body2>
          <Body2>
            Status:{' '}
            <Body2 as='span' bold className='mx-1'>
              {reportDetails.verifyResult.record.recordStatus}
            </Body2>
          </Body2>
        </header>
        <table>
          <tbody>
            <tr>
              <td>Country:</td>
              <td>{reportDetails.verifyResult.countryCode}</td>
            </tr>
            <tr>
              <td>Transaction Records ID:</td>
              <td>{reportDetails.verifyResult.transactionID}</td>
            </tr>
            <tr>
              <td>Transaction Date:</td>
              <td>{formatLocalDateUTC(reportDetails.verifyResult.uploadedDt, true)}</td>
            </tr>
            <tr>
              <td>Verification Rule:</td>
              <td>{reportDetails.verifyResult.record.rule.ruleName}</td>
            </tr>
          </tbody>
        </table>
        <Heading3 className='my-8 flex items-center justify-center'>Match Indicators</Heading3>
        <table>
          <thead>
            <tr>
              <th>Fields</th>
              {!!reportDetails.verifyResult.record.normalizedResults.length &&
                reportDetails.verifyResult.record.normalizedResults[0].dataSources.map((data) => (
                  <th key={data.name}>{data.name}</th>
                ))}
            </tr>
          </thead>
          <tbody>
            {reportDetails.verifyResult.record.normalizedResults.map((result, i) => {
              return (
                <tr key={i}>
                  <td key={result.name}>
                    <Body2>{result.name}</Body2>
                    <br />
                    <Body2 bold>{result.value}</Body2>
                  </td>
                  {result.dataSources.map((x) => {
                    return (
                      <td key={x.name}>
                        {x.result === 'match' ? (
                          <CheckmarkIcon />
                        ) : x.result === 'nomatch' ? (
                          <RemoveIcon />
                        ) : x.result === 'missing' ? (
                          'missing'
                        ) : null}
                      </td>
                    )
                  })}
                </tr>
              )
            })}
          </tbody>
        </table>
        <Heading3 className='my-8 flex items-center justify-center'>Appended Information</Heading3>
        <table>
          <thead>
            <tr>
              <th>DataSource</th>
              <th>Result Found</th>
              <th>Value</th>
            </tr>
          </thead>
          <tbody>
            {reportDetails.verifyResult.record.normalizedAppendedFields.map((result, i) => {
              return (
                <tr key={i}>
                  <td key={result.datasource}>{result.datasource}</td>
                  <td key={result.fieldName}>{result.fieldName}</td>
                  <td key={result.value}>{result.value}</td>
                </tr>
              )
            })}
          </tbody>
        </table>
        <Heading3 className='my-8 flex items-center justify-center'>Verification Details</Heading3>
        <table>
          <thead>
            <tr>
              <th style={{ width: '25%' }}>DataSource</th>
              <th style={{ width: '10%' }}>Result Found</th>
              <th style={{ width: '65%' }}>Errors</th>
            </tr>
          </thead>
          <tbody>
            {reportDetails.verifyResult.record.normalizedDatasourceStatus.map((status, i) => {
              return (
                <tr key={i}>
                  <td key={status.name}>{status.name}</td>
                  <td key={status.result}>{status.result}</td>
                  <td>
                    {status.errors.map((x) => (
                      <div>
                        Error: {x.errorCode}, Message: {x.message},
                      </div>
                    ))}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </ReportWrapper>
    </Wrapper>
  )
}
export default ClientDetailsVerificationReportDetails
